import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useWizard } from "../../context/WizardContext";
import { CurrencyInput } from "../shared/CurrencyInput";
import type { BusinessOtherExpenses, FinancialInfo } from "../../types/business.types";
import { cn } from "@/lib/utils";

const OTHER_EXPENSE_FIELDS: { key: keyof Omit<BusinessOtherExpenses, "totalExpenses">; label: string }[] = [
  { key: "alarmMonitoring", label: "Alarm Monitoring" },
  { key: "automobileExpensesMileage", label: "Automobile Expenses (Mileage)" },
  { key: "computersAndSupplies", label: "Computers and Supplies" },
  { key: "furniture", label: "Furniture" },
  { key: "insurance", label: "Insurance" },
  { key: "autoInsurance", label: "Auto Insurance" },
  { key: "insurancesOther", label: "Other Insurances" },
  { key: "gas", label: "Gas" },
  { key: "highwayToll", label: "Highway Toll" },
  { key: "licenseAndPermits", label: "License and Permits" },
  { key: "misc", label: "Misc" },
  { key: "officeSupplies", label: "Office Supplies" },
  { key: "rent", label: "Rent" },
  { key: "repairs", label: "Repairs" },
  { key: "equipmentRepairs", label: "Equipment Repairs" },
  { key: "telephone", label: "Telephone" },
  { key: "internet", label: "Internet" },
  { key: "travelAndEnt", label: "Travel and Entertainment" },
  { key: "meals", label: "Meals" },
  { key: "travelAndEntertainment", label: "Travel and Entertainment (Other)" },
  { key: "utilities", label: "Utilities" },
  { key: "gasAndElectric", label: "Gas and Electric" },
  { key: "utilitiesAndOther", label: "Utilities and Other" },
  { key: "accountingFees", label: "Accounting Fees" },
];

const ACCEPTED_TYPES = { "application/pdf": [".pdf"], "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] };
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

export function BusinessStep6OtherAndDocuments() {
  const { businessData, updateBusinessData } = useWizard();
  const other = businessData.otherExpenses ?? {} as BusinessOtherExpenses;
  const fin = businessData.financialInfo ?? {} as FinancialInfo;

  const setOther = (patch: Partial<BusinessOtherExpenses>) => {
    updateBusinessData({ otherExpenses: { ...other, ...patch } });
  };

  const setFinancial = (patch: Partial<FinancialInfo>) => {
    updateBusinessData({ financialInfo: { ...fin, ...patch } });
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // TODO: upload via API and attach to draft; for now just acknowledge
    if (acceptedFiles.length > 0) {
      console.log("Files for upload:", acceptedFiles.map((f) => f.name));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_SIZE,
    multiple: true,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Other Expenses & Documents</h2>
        <p className="text-sm text-muted-foreground">
          Additional expense line items, summary, and document upload.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Other Expense Line Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            {OTHER_EXPENSE_FIELDS.map(({ key, label }) => (
              <CurrencyInput
                key={key}
                label={label}
                value={other[key] as number | undefined}
                onChange={(v) => setOther({ [key]: v } as Partial<BusinessOtherExpenses>)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <CurrencyInput
            label="Total Expenses"
            value={other.totalExpenses}
            onChange={(v) => setOther({ totalExpenses: v })}
          />
          <CurrencyInput
            label="Bank balance as of 12/31"
            value={fin.yearEndBankBalance}
            onChange={(v) => setFinancial({ yearEndBankBalance: v })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Any Other Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="additionalNotes">Additional notes</Label>
            <Textarea
              id="additionalNotes"
              value={fin.additionalNotes ?? ""}
              onChange={(e) => setFinancial({ additionalNotes: e.target.value })}
              placeholder="Any other details you want to include..."
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Document Upload</CardTitle>
          <p className="text-sm text-muted-foreground">
            Drag and drop or click to upload. PDF, JPG, PNG (max 10MB each).
          </p>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={cn(
              "flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30 p-8 transition-colors cursor-pointer hover:border-primary/50 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isDragActive && "border-primary bg-primary/5"
            )}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-full bg-muted p-4 text-muted-foreground">
                {isDragActive ? (
                  <FileText className="h-8 w-8" />
                ) : (
                  <Upload className="h-8 w-8" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {isDragActive ? "Drop files here" : "Drag & drop files here, or click to browse"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG (max 10MB)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
